import {
  Card,
  Dashboard,
  DashboardAffordancesType,
  DashboardOwner,
  DashboardUserPermission,
  DashboardViewer,
  DashboardWithRelationships,
  LockedFieldKeys,
  NewDashboard,
} from 'utils/customTypes';
import config from 'config/Config';
import { AxiosInstance } from 'axios';
import { initAxios } from 'utils/axios';
import {
  JsonApiResource,
  JsonApiResourceIdentifier,
  JsonApiResponse,
} from 'types/store/jsonApi';
import { DashboardFilters } from 'utils/types/dashboardFilters';
import { JsonApiType } from 'utils/constants/api';
import { serializeParamsWithArray } from '../utils';

export interface DashboardOwnerApiResponse {
  id: string;
  externalId: string;
  organizationId: string;
  externalOrganizationId: string;
  status: string;
  firstName: string;
  lastName: string;
  email: string;
  disabledAt?: string | null;
}

export interface DashboardAttributes {
  name: string;
  permission?: DashboardUserPermission;
  createdAt: string;
  updatedAt: string;
  owners: DashboardOwnerApiResponse[];
}

export interface DashboardListJsonApiResponse
  extends JsonApiResponse<DashboardAttributes> {
  data: JsonApiResource<DashboardAttributes>[];
}

export interface DashboardListJsonApiResponseWithOwners
  extends DashboardListJsonApiResponse {
  included?: JsonApiResource<DashboardOwnerApiResponse>[];
}

export interface DashboardJsonApiResponse
  extends JsonApiResponse<DashboardAttributes> {
  data: JsonApiResource<DashboardAttributes>;
}

export interface DashboardJsonApiResponseWithOwners
  extends DashboardJsonApiResponse {
  included?: JsonApiResource<DashboardOwnerApiResponse>[];
}

export interface DashboardJsonApiResponseWithFilters
  extends DashboardJsonApiResponse {
  included?: JsonApiResource<FilterAttributes>[];
}

export interface DashboardWithRelationshipsJsonApiResponse
  extends JsonApiResponse<DashboardAttributes> {
  data: JsonApiResource<DashboardAttributes>;
}

export interface CardAttributes {
  column: number;
  row: number;
  width: number;
  height: number;
}

export interface CardTypeAttributes {
  insightType: string;
  nameKey: string;
  descriptionKey: string;
  maxHeight: number;
  minHeight: number;
  maxWidth: number;
  minWidth: number;
  defaultHeight: number;
  defaultWidth: number;
  dataUrl: string;
}

export interface DashboardWithCardsJsonApiResponse
  extends JsonApiResponse<CardAttributes> {
  data: JsonApiResource<CardAttributes>[];
  included?: JsonApiResource<CardTypeAttributes>[];
}

export interface FilterAttributes {
  filters: DashboardFilters;
}

export interface DashboardWithFiltersJsonApiResponse
  extends JsonApiResponse<FilterAttributes> {
  data: JsonApiResource<FilterAttributes>;
}

export interface CardJsonApiResponse extends JsonApiResponse<CardAttributes> {
  data: JsonApiResource<CardAttributes>;
  included?: JsonApiResource<CardTypeAttributes>[];
}

export interface ViewerAttributes {
  externalId: string;
  organizationId: string | null;
  externalOrganizationId: string;
  version: number;
  type: string;
  status: string;
  firstName: string;
  lastName: string;
  email: string;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
  disabledAt: string | null;
}

export interface ViewersListJsonApiResponse
  extends JsonApiResponse<ViewerAttributes> {
  data: JsonApiResource<ViewerAttributes>[];
}

export enum DashboardSubPath {
  CARDS = 'cards',
  FILTERS = 'dashboard-filters',
}

export enum DashboardInclude {
  FILTERS = 'dashboard-filters',
  OWNERS = 'owners',
}

// Makes `token` required only if `T` contains locked fields
export type UpdateParams<T extends Partial<Dashboard>> = Extract<
  keyof T,
  LockedFieldKeys
> extends never
  ? { dashboardId: string; update: T; token?: string }
  : { dashboardId: string; update: T; token: string };

export interface DashboardAPI {
  getList(): Promise<{
    data: Dashboard[];
    affordances: DashboardAffordancesType;
  }>;
  getById(dashboardId: string): Promise<Dashboard>;
  getByIdWithAllRelationships(
    dashboardId: string
  ): Promise<DashboardWithRelationships>;
  getByIdWithRelationships(
    dashboardId: string,
    relationship: DashboardSubPath
  ): Promise<DashboardWithRelationships>;
  createDashboard: (
    createUrl: string,
    newDashboardData: NewDashboard
  ) => Promise<DashboardWithRelationships>;
  createThroughDuplication: (
    dashboardId: string,
    newDashboardData: NewDashboard
  ) => Promise<DashboardWithRelationships>;
  updateDashboard<T extends Partial<Dashboard>>(
    updateUrl: string,
    params: UpdateParams<T>
  ): Promise<Partial<Dashboard>>;
  deleteDashboard: (deleteUrl: string) => Promise<boolean>;
  getDashboardFilters: (dashboardId: string) => Promise<DashboardFilters>;
  saveDashboardFilters: (
    filtersUrl: string,
    filters: DashboardFilters
  ) => Promise<DashboardFilters>;
  deleteDashboardFilters: (filtersUrl: string) => Promise<DashboardFilters>;
  createDashboardCard: (newCard: Card) => Promise<Card>;
  updateDashboardCard: (
    cardId: string,
    update: Partial<Card> & Required<Pick<Card, 'dashboardId'>>
  ) => Promise<Card>;
  deleteDashboardCard: (
    dashboardId: string,
    cardId: string
  ) => Promise<boolean>;
  getViewers(): Promise<DashboardViewer[]>;
  shareDashboard(shareUrl: string, userIds: string[]): Promise<string[]>;
  deleteViewer(shareUrl: string, viewerId: string): Promise<void>;
}

export class AxiosDashboardAPI implements DashboardAPI {
  private axiosInstance: AxiosInstance = initAxios(
    config.get('dashboardBackendURL'),
    'dashboard'
  );
  private dashboardPathPrefix: string = 'dashboards';
  private dashboardsPath: string = `${this.dashboardPathPrefix}/`;
  private dashboardViewersPath: string = 'dashboard-viewers';

  private dashboardPathBuilder(dashboardId: string) {
    return `${this.dashboardsPath}${dashboardId}`;
  }

  private dashboardSubPathBuilder(
    dashboardId: string,
    subPath: DashboardSubPath,
    subPathId?: string
  ) {
    const pathParts = [this.dashboardPathPrefix, dashboardId, subPath];
    if (subPathId) {
      pathParts.push(subPathId);
    }
    return pathParts.join('/');
  }

  private getIncludedOwners(
    included?: JsonApiResource<DashboardOwnerApiResponse>[]
  ): DashboardOwner[] {
    if (!included) {
      return [];
    }

    return included
      .filter((resource) => resource.type === 'owners')
      .map((resource) => ({
        id: resource.id,
        externalId: resource.attributes.externalId,
        organizationId: resource.attributes.organizationId,
        externalOrganizationId: resource.attributes.externalOrganizationId,
        status: resource.attributes.status,
        firstName: resource.attributes.firstName,
        lastName: resource.attributes.lastName,
        email: resource.attributes.email,
        disabled:
          resource.attributes.status === 'disabled' ||
          !!resource.attributes.disabledAt,
      }));
  }

  async getById(dashboardId: string): Promise<Dashboard> {
    const response = await this.axiosInstance.get<DashboardJsonApiResponse>(
      this.dashboardPathBuilder(dashboardId)
    );

    const {
      data: { id, attributes },
    } = response.data;

    return {
      id: id,
      name: attributes.name,
      createdAt: attributes.createdAt,
      updatedAt: attributes.updatedAt,
    };
  }

  async getList(): Promise<{
    data: Dashboard[];
    affordances: DashboardAffordancesType;
  }> {
    const response =
      await this.axiosInstance.get<DashboardListJsonApiResponseWithOwners>(
        this.dashboardsPath,
        {
          params: {
            include: DashboardInclude.OWNERS,
          },
        }
      );

    const { data, included, links } = response.data;

    const includedOwners = this.getIncludedOwners(included);

    const dashboards: Dashboard[] = data.map(
      ({ id, attributes, relationships }) => {
        const ownerRelationships = relationships?.owners?.data as
          | JsonApiResourceIdentifier[]
          | undefined;
        const ownerIds = ownerRelationships?.map((owner) => owner.id) ?? [];

        return {
          id,
          name: attributes.name,
          permission: attributes.permission ?? DashboardUserPermission.VIEWER,
          createdAt: attributes.createdAt,
          updatedAt: attributes.updatedAt,
          owners: includedOwners.filter((owner) => ownerIds.includes(owner.id)),
        };
      }
    );

    const affordances: DashboardAffordancesType = {
      global: links ?? {},
      resources: {},
    };

    for (const resource of data) {
      if (resource.links && Object.keys(resource.links).length > 0) {
        affordances.resources[resource.id] = resource.links;
      }
    }

    return {
      data: dashboards,
      affordances,
    };
  }

  async getByIdWithAllRelationships(
    dashboardId: string
  ): Promise<DashboardWithRelationships> {
    const dashboardResponse =
      await this.axiosInstance.get<DashboardJsonApiResponseWithFilters>(
        this.dashboardPathBuilder(dashboardId),
        {
          params: {
            include: [DashboardInclude.FILTERS, DashboardInclude.OWNERS],
          },
          paramsSerializer: (params) =>
            serializeParamsWithArray(params, 'comma'),
        }
      );

    const cardsResponse =
      await this.axiosInstance.get<DashboardWithCardsJsonApiResponse>(
        this.dashboardSubPathBuilder(dashboardId, DashboardSubPath.CARDS)
      );

    const {
      data: { id: returnedDashboardId, attributes },
      links,
      included: filtersData,
    } = dashboardResponse.data;
    const { data: cardsData } = cardsResponse.data;

    const dashboard: Dashboard = {
      id: returnedDashboardId,
      name: attributes.name,
      createdAt: attributes.createdAt,
      updatedAt: attributes.updatedAt,
    };

    const cards: Card[] = cardsData.map(({ id, attributes, relationships }) => {
      const cardTypeRelationship = relationships?.cardType;
      if (
        !cardTypeRelationship?.data ||
        Array.isArray(cardTypeRelationship.data)
      ) {
        throw new Error(`Card ${id} is missing required cardType relationship`);
      }
      const cardTypeId = cardTypeRelationship.data.id;

      return {
        id: id,
        cardTypeId,
        x: attributes.column,
        y: attributes.row,
        width: attributes.width,
        height: attributes.height,
        dashboardId: returnedDashboardId,
      };
    });

    let filters: DashboardFilters = {};
    if (filtersData && filtersData.length > 0) {
      filters = filtersData[0].attributes.filters;
    }

    return {
      dashboard,
      cards,
      filters,
      resourceAffordances: links,
    };
  }

  async getByIdWithRelationships(
    dashboardId: string,
    relationship: DashboardSubPath
  ): Promise<DashboardWithRelationships> {
    switch (relationship) {
      case DashboardSubPath.CARDS:
        return this.getByIdWithCards(dashboardId);
      default:
        throw new Error(`Unsupported relationship: ${relationship}`);
    }
  }

  async getByIdWithCards(
    dashboardId: string
  ): Promise<DashboardWithRelationships> {
    // First get the dashboard information
    const dashboardResponse =
      await this.axiosInstance.get<DashboardJsonApiResponse>(
        this.dashboardPathBuilder(dashboardId)
      );

    // Then get the cards for this dashboard
    const cardsResponse =
      await this.axiosInstance.get<DashboardWithCardsJsonApiResponse>(
        this.dashboardSubPathBuilder(dashboardId, DashboardSubPath.CARDS)
      );

    const {
      data: { id: returnedDashboardId, attributes },
      links,
    } = dashboardResponse.data;
    const { data: cardsData } = cardsResponse.data;

    const dashboard: Dashboard = {
      id: returnedDashboardId,
      name: attributes.name,
      createdAt: attributes.createdAt,
      updatedAt: attributes.updatedAt,
    };

    const cards: Card[] = cardsData.map(({ id, attributes, relationships }) => {
      const cardTypeRelationship = relationships?.cardType;
      if (
        !cardTypeRelationship?.data ||
        Array.isArray(cardTypeRelationship.data)
      ) {
        throw new Error(`Card ${id} is missing required cardType relationship`);
      }
      const cardTypeId = cardTypeRelationship.data.id;

      return {
        id: id,
        cardTypeId,
        x: attributes.column,
        y: attributes.row,
        width: attributes.width,
        height: attributes.height,
        dashboardId: returnedDashboardId,
      };
    });

    return {
      dashboard,
      cards,
      resourceAffordances: links,
    };
  }

  async createDashboard(
    createUrl: string,
    newDashboardData: NewDashboard
  ): Promise<DashboardWithRelationships> {
    if (!createUrl) {
      throw new Error('Create URL is required');
    }
    const response =
      await this.axiosInstance.post<DashboardJsonApiResponseWithOwners>(
        createUrl,
        newDashboardData,
        {
          params: {
            include: DashboardInclude.OWNERS,
          },
        }
      );

    const {
      data: { id, attributes, relationships },
      included,
    } = response.data;

    const includedOwners = this.getIncludedOwners(included);

    const ownerRelationships = relationships?.owners?.data as
      | JsonApiResourceIdentifier[]
      | undefined;
    const ownerIds = ownerRelationships?.map((owner) => owner.id) ?? [];

    const createdDashboard: Dashboard = {
      id: id,
      name: attributes.name,
      createdAt: attributes.createdAt,
      updatedAt: attributes.updatedAt,
      permission: attributes.permission ?? DashboardUserPermission.VIEWER,
      owners: includedOwners.filter((owner) => ownerIds.includes(owner.id)),
    };

    return {
      dashboard: createdDashboard,
      cards: [],
    };
  }

  async createThroughDuplication(
    dashboardId: string,
    newDashboardData: NewDashboard
  ): Promise<DashboardWithRelationships> {
    throw new Error('Not implemented');
  }

  async updateDashboard<T extends Partial<Dashboard>>(
    updateUrl: string,
    params: UpdateParams<T>
  ): Promise<Partial<Dashboard>> {
    if (!updateUrl) {
      throw new Error('Update URL is required');
    }
    const response = await this.axiosInstance.patch<DashboardJsonApiResponse>(
      updateUrl,
      params.update
    );

    const {
      data: { id, attributes },
    } = response.data;

    return {
      id: id,
      name: attributes.name,
      createdAt: attributes.createdAt,
      updatedAt: attributes.updatedAt,
    };
  }

  async deleteDashboard(deleteUrl: string): Promise<boolean> {
    if (!deleteUrl) {
      throw new Error('Delete URL is required');
    }
    await this.axiosInstance.delete(deleteUrl);
    return true;
  }

  async saveDashboardFilters(
    filtersUrl: string,
    filters: DashboardFilters
  ): Promise<DashboardFilters> {
    const response =
      await this.axiosInstance.post<DashboardWithFiltersJsonApiResponse>(
        filtersUrl,
        { filters }
      );

    return response.data.data.attributes?.filters ?? {};
  }

  async deleteDashboardFilters(filtersUrl: string): Promise<DashboardFilters> {
    const response =
      await this.axiosInstance.delete<DashboardWithFiltersJsonApiResponse>(
        filtersUrl
      );

    return response.data.data.attributes?.filters ?? {};
  }

  async getDashboardFilters(dashboardId: string): Promise<DashboardFilters> {
    const response =
      await this.axiosInstance.get<DashboardWithFiltersJsonApiResponse>(
        this.dashboardSubPathBuilder(dashboardId, DashboardSubPath.FILTERS)
      );

    return response.data.data.attributes?.filters ?? {};
  }

  dashboardCardResponseHandler = (response: CardJsonApiResponse): Card => {
    const { id, attributes, relationships } = response.data;
    const cardTypeRelationship = relationships?.cardType;
    const dashboardRelationship = relationships?.dashboard;

    if (
      !cardTypeRelationship?.data ||
      Array.isArray(cardTypeRelationship.data) ||
      !dashboardRelationship?.data ||
      Array.isArray(dashboardRelationship.data)
    ) {
      throw new Error(`Card ${id} is missing required relationship`);
    }

    const cardTypeId = cardTypeRelationship.data.id;
    const returnedDashboardId = dashboardRelationship.data.id;
    return {
      id: id,
      cardTypeId,
      x: attributes.column,
      y: attributes.row,
      width: attributes.width,
      height: attributes.height,
      dashboardId: returnedDashboardId,
    };
  };

  async createDashboardCard(card: Card): Promise<Card> {
    const response = await this.axiosInstance.post<CardJsonApiResponse>(
      this.dashboardSubPathBuilder(card.dashboardId, DashboardSubPath.CARDS),
      {
        data: {
          type: JsonApiType.CARD,
          attributes: {
            width: card.width,
            height: card.height,
            column: card.x,
            row: card.y,
          },
          relationships: {
            cardType: {
              data: { type: JsonApiType.CARD_TYPE, id: card.cardTypeId },
            },
          },
        },
      }
    );
    return this.dashboardCardResponseHandler(response.data);
  }

  async updateDashboardCard(
    cardId: string,
    update: Partial<Card> & Required<Pick<Card, 'dashboardId'>>
  ): Promise<Card> {
    const response = await this.axiosInstance.patch<CardJsonApiResponse>(
      this.dashboardSubPathBuilder(
        update.dashboardId,
        DashboardSubPath.CARDS,
        cardId
      ),
      {
        width: update.width,
        height: update.height,
        column: update.x,
        row: update.y,
        cardTypeId: update.cardTypeId,
      }
    );
    return this.dashboardCardResponseHandler(response.data);
  }

  async deleteDashboardCard(
    dashboardId: string,
    cardId: string
  ): Promise<boolean> {
    await this.axiosInstance.delete(
      this.dashboardSubPathBuilder(dashboardId, DashboardSubPath.CARDS, cardId)
    );
    return true;
  }

  async shareDashboard(shareUrl: string, userIds: string[]): Promise<string[]> {
    const response = await this.axiosInstance.post<DashboardJsonApiResponse>(
      shareUrl,
      { userIds }
    );
    const viewerRelationships =
      response.data.data.relationships?.['dashboard-viewers']?.data;
    if (!Array.isArray(viewerRelationships)) {
      return [];
    }
    return viewerRelationships.map((r) => r.id);
  }

  async deleteViewer(shareUrl: string, viewerId: string): Promise<void> {
    await this.axiosInstance.delete(`${shareUrl}/${viewerId}`);
  }

  async getViewers(): Promise<DashboardViewer[]> {
    const response = await this.axiosInstance.get<ViewersListJsonApiResponse>(
      this.dashboardViewersPath
    );

    return response.data.data.map(({ id, attributes, relationships }) => {
      const viewerRelationships = relationships?.['dashboards-viewer']?.data;
      const sharedDashboardIds = Array.isArray(viewerRelationships)
        ? viewerRelationships.map((r) => r.id)
        : [];

      return {
        id,
        externalId: attributes.externalId,
        organizationId: attributes.organizationId,
        externalOrganizationId: attributes.externalOrganizationId,
        version: attributes.version,
        type: attributes.type,
        status: attributes.status,
        firstName: attributes.firstName,
        lastName: attributes.lastName,
        email: attributes.email,
        createdAt: attributes.createdAt,
        updatedAt: attributes.updatedAt,
        deletedAt: attributes.deletedAt,
        disabledAt: attributes.disabledAt,
        sharedDashboardIds,
      };
    });
  }
}
